/**
 * Quote versions (E4, spec D20 §7, §9.3): what we prepared and sent the customer, version
 * by version, and what they answered. Marking a version sent or recording a response
 * writes the ledger event the record's process waits on, so the map moves.
 *
 * Nothing is sent from here: preparing renders the quotation and drafts the offer email on the
 * record's thread (PR 2); a person sends it from the Inbox, which marks it sent, or sends it their
 * own way and marks it sent here.
 * A version is prepared and sent only where the record's process waits for one
 * (SA-1003): elsewhere the form gives way to one line saying where.
 */
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Input, Textarea } from '@/components/ui'
import { buttonVariants } from '@/components/ui/basis/button'
import { cn } from '@/utils'
import { formatCurrency } from '@/utils/currency'
import { useDefaultTimezone } from '@/hooks/useDefaultTimezone'
import {
  downloadQuoteDocument, fetchQuoteDocument, recordErrorMessage, useMarkQuoteSent, usePrepareQuote, useQuoteVersions,
  useDismissCustomerUpdate, useRecordQuoteResponse, useRedraftQuote, useUpdatePreparedQuote,
} from '@/hooks/useRecordExtras'
import { readSummary, readUnavailable } from './readState'
import { useOwnedSections } from './laneDefaults'
import { RecordSection } from './RecordSection'
import { quotesSummary } from './sectionSummaries'
import { MediaViewerDialog } from '@/components/inbox/MediaViewerDialog'
import { canShow } from '@/utils/mediaView'
import { whenText } from '@/components/process/stepLiveText'
import { cannotRecord, responseKindOf } from './waitingResponses'
import { cannotRecordText, DISMISS, OPEN_IN_INBOX, RESPONSE_NOT_WAITING, updateBlockHeading } from './customerUpdateText'
import { ApiError } from '@/services/apiClient'
import * as text from './quoteText'
import type { QuoteVersion } from '@/types/commerce'

type ResponseKind = 'counter' | 'accepted' | 'declined'

const RESPONSE_BUTTON: Record<ResponseKind, string> = { counter: 'Counter', accepted: 'Accept', declined: 'Decline' }
const RESPONSE_SUBMIT: Record<ResponseKind, string> = {
  counter: 'Record counter', accepted: 'Record accepted', declined: 'Record declined',
}

function toCents(text: string): number | null {
  const cents = Math.round(parseFloat(text) * 100)
  return Number.isFinite(cents) && cents > 0 ? cents : null
}

function chip(v: QuoteVersion): { text: string; tone: string } {
  if (v.expired) return { text: 'Expired', tone: 'bg-destructive/10 text-destructive' }
  switch (v.status) {
    case 'prepared': return { text: text.PREPARED_CHIP, tone: 'bg-muted text-muted-foreground' }
    case 'sent':
      return { text: `Sent · valid until ${new Date(v.validUntil).toLocaleString()}`, tone: 'bg-info/10 text-info' }
    case 'countered':
      return {
        text: v.response?.amountCents != null
          ? `Countered ${formatCurrency(v.response.amountCents, v.currency)}` : 'Countered',
        tone: 'bg-warning/10 text-warning-strong',
      }
    case 'accepted': return { text: 'Accepted', tone: 'bg-success/10 text-success' }
    case 'declined': return { text: 'Declined', tone: 'bg-destructive/10 text-destructive' }
    case 'superseded': return { text: 'Superseded', tone: 'bg-muted text-muted-foreground' }
    default: return { text: v.status, tone: 'bg-muted text-muted-foreground' }
  }
}

function dismissErrorMessage(err: unknown): string {
  return err instanceof ApiError && err.detail === 'not_waiting' ? RESPONSE_NOT_WAITING : recordErrorMessage(err)
}

export function QuoteVersionsCard({
  orderId, currency, open, focused = false, onChanged,
}: {
  orderId: string
  currency: string
  /** The record is open: a closed record lists its versions, read-only. */
  open: boolean
  focused?: boolean
  /** A write landed: the page refetches the record and its process. */
  onChanged: () => void
}) {
  const quotes = useQuoteVersions(orderId)
  const owned = useOwnedSections(orderId)
  const zone = useDefaultTimezone()
  const now = new Date()
  const prepare = usePrepareQuote(orderId)
  const update = useUpdatePreparedQuote(orderId)
  const markSent = useMarkQuoteSent(orderId)
  const respond = useRecordQuoteResponse(orderId)
  const redraft = useRedraftQuote(orderId)
  const dismiss = useDismissCustomerUpdate(orderId)
  const [dismissing, setDismissing] = useState<string | null>(null)
  const [dismissReason, setDismissReason] = useState('')
  const amountRef = useRef<HTMLInputElement>(null)
  const noteRef = useRef<HTMLTextAreaElement>(null)
  const focusPending = useRef(false)
  const [amount, setAmount] = useState('')
  const [validity, setValidity] = useState('72')
  const [terms, setTerms] = useState('')
  const [editing, setEditing] = useState(false)
  const [keptDraftId, setKeptDraftId] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [note, setNote] = useState('')
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const [responding, setResponding] = useState<ResponseKind | null>(null)
  const [responseAmount, setResponseAmount] = useState('')
  const [responseNote, setResponseNote] = useState('')
  // D11: the approved price pre-fills an untouched Amount once; typing over it drops the chip.
  const seeded = useRef<string | null>(null)
  const touched = useRef(false)
  const suggested = quotes.data?.suggestedAmountCents ?? null
  useEffect(() => {
    if (editing || touched.current || suggested == null) return
    if (amount !== '' && amount !== seeded.current) return
    const next = (suggested / 100).toString()
    if (next === seeded.current && amount === next) return
    seeded.current = next
    setAmount(next)
  }, [suggested, amount, editing])
  const fromApproval = seeded.current !== null && amount === seeded.current

  const versions = [...(quotes.data?.versions ?? [])].sort((a, b) => b.versionNumber - a.versionNumber)
  const current = quotes.data?.current ?? null
  const prepared = quotes.data?.prepared ?? null
  // SA-1003: the send's own rule, served by the read — a version is prepared and sent
  // only where the record's process waits for one.
  const sendRefusal = quotes.data?.sendRefusal ?? null
  // SA-1016: the response's own rule — an answer is recorded only where the
  // record's process waits for one.
  const respondRefusal = quotes.data?.respondRefusal ?? null
  const draftRefusal = quotes.data?.draftRefusal ?? null
  const answerable = open && current?.status === 'sent' ? current : null
  const amountCents = toCents(amount)
  const hours = parseInt(validity, 10)
  const waitingResponses = quotes.data?.waitingResponses ?? []
  const busy = prepare.isPending || update.isPending || markSent.isPending || respond.isPending || redraft.isPending

  // SA-1047: Record <kind> chooses the answer, then the amount (a counter) or the note takes the focus.
  useEffect(() => {
    if (!focusPending.current || !responding) return
    focusPending.current = false
    ;(responding === 'counter' ? amountRef.current : noteRef.current)?.focus()
  }, [responding])
  const handleRecordWaiting = (kind: ResponseKind) => { focusPending.current = true; setResponding(kind) }
  const handleDismissWaiting = (updateId: string) => {
    dismiss.reset()
    dismiss.mutate({ updateId, reason: dismissReason }, { onSuccess: () => { setDismissing(null); setDismissReason(''); onChanged() } })
  }

  const resetForm = () => { setAmount(''); setTerms(''); setValidity('72'); setEditing(false); setKeptDraftId(null)
    seeded.current = null; touched.current = false }
  // SA-1419: a write that succeeded leaves no other write's refusal on the card.
  const clearWriteErrors = () => { prepare.reset(); update.reset(); markSent.reset(); respond.reset(); redraft.reset() }
  const afterWrite = () => {
    // SA-1016: a new version is a new question — no answer half-typed,
    // no refusal left over from the version it superseded.
    setResponding(null); setResponseAmount(''); setResponseNote(''); clearWriteErrors()
    // The kept-text warning belongs to the edit that answered it, never to a later write.
    setKeptDraftId(null)
    onChanged()
  }
  const handlePrepare = () => {
    if (amountCents == null || !(hours > 0)) return
    prepare.mutate({ amountCents, validityHours: hours, terms: terms.trim() || null },
      { onSuccess: () => { resetForm(); afterWrite() } })
  }
  const startEdit = () => {
    if (!prepared) return
    setAmount((prepared.amountCents / 100).toString()); setValidity(String(prepared.validityHours)); setTerms(prepared.terms ?? '')
    // An open "Mark as sent" confirm would let Confirm sent go with the edits unsaved on screen.
    setConfirming(false)
    redraft.reset()
    setEditing(true)
  }
  const handleSaveEdit = () => {
    if (!prepared || amountCents == null || !(hours > 0)) return
    redraft.reset()
    update.mutate({ versionNumber: prepared.versionNumber, amountCents, validityHours: hours, terms: terms.trim() || null },
      { onSuccess: (data) => {
        clearWriteErrors(); resetForm(); onChanged()
        const d = data.prepared?.draft
        setKeptDraftId(d && d.status === 'pending' && d.edited ? d.id : null)
      } })
  }
  // A redraft makes a new draft: the warning about the draft an edit kept no longer applies.
  const handleRedraft = (v: QuoteVersion) => { setKeptDraftId(null); redraft.mutate({ versionNumber: v.versionNumber }, { onSuccess: clearWriteErrors }) }
  const handleMarkSent = () => {
    if (!prepared) return
    update.reset()
    markSent.mutate({ versionNumber: prepared.versionNumber, note: note.trim() || null },
      { onSuccess: () => { setConfirming(false); setNote(''); resetForm(); afterWrite() } })
  }
  // A download error belongs to the versions it was raised on.
  const versionsKey = (quotes.data?.versions ?? []).map(v => `${v.id}:${v.document?.name ?? ''}`).join('|')
  useEffect(() => { setDownloadError(null) }, [versionsKey])
  const handleDownload = async (v: QuoteVersion) => {
    if (!v.document) return
    setDownloadError(null)
    try { await downloadQuoteDocument(orderId, v.versionNumber, v.document.name) } catch { setDownloadError(text.DOWNLOAD_FAILED) }
  }

  // SA-1331: View PDF opens the quotation in the Inbox's viewer over a blob; a browser that cannot
  // show a PDF downloads it instead, View PDF spinning meanwhile (SA-1333).
  const [viewing, setViewing] = useState<{ url: string; name: string; size: number | null } | null>(null)
  const [viewBusy, setViewBusy] = useState<number | null>(null)
  useEffect(() => () => { if (viewing) URL.revokeObjectURL(viewing.url) }, [viewing])
  // SA-1333: a card gone before the document arrives makes no object URL, so none leaks.
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])
  const handleView = async (v: QuoteVersion) => {
    if (!v.document) return
    setDownloadError(null)
    setViewBusy(v.versionNumber)
    try {
      if (!canShow('pdf', 'application/pdf')) { await handleDownload(v); return }
      const blob = await fetchQuoteDocument(orderId, v.versionNumber)
      if (!mounted.current) return
      setViewing({ url: URL.createObjectURL(blob), name: v.document.name, size: v.document.byteSize ?? null })
    } catch { if (mounted.current) setDownloadError(text.VIEW_FAILED) }
    finally { if (mounted.current) setViewBusy(null) }
  }

  const handleRespond = () => {
    if (!answerable || !responding) return
    const counter = responding === 'counter' ? toCents(responseAmount) : null
    if (responding === 'counter' && counter == null) return
    respond.mutate(
      { versionNumber: answerable.versionNumber, kind: responding, amountCents: counter, note: responseNote.trim() || null },
      { onSuccess: () => { setResponding(null); setResponseAmount(''); setResponseNote(''); clearWriteErrors(); onChanged() } },
    )
  }

  const form = (submitLabel: string, onSubmit: () => void, cancel?: () => void) => (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-40">
          <Input aria-label={text.AMOUNT_LABEL} type="number" min={0} step="0.01" placeholder="Amount"
            value={amount} onChange={e => { touched.current = true; setAmount(e.target.value) }} />
        </div>
        <span className="text-sm text-muted-foreground">{currency}</span>
        {fromApproval && <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">{text.FROM_APPROVAL}</span>}
        <div className="w-28">
          <Input aria-label={text.VALIDITY_LABEL} type="number" min={1} step="1"
            value={validity} onChange={e => setValidity(e.target.value)} />
        </div>
        <span className="text-sm text-muted-foreground">hours</span>
      </div>
      <Textarea aria-label={text.REMARKS_LABEL} rows={2} placeholder="Remarks (optional)"
        value={terms} onChange={e => setTerms(e.target.value)} />
      <div className="flex gap-2">
        <Button size="sm" onClick={onSubmit} disabled={busy || amountCents == null || !(hours > 0)}>{submitLabel}</Button>
        {cancel && <Button size="sm" variant="secondary" onClick={cancel} disabled={busy}>{text.CANCEL_EDIT}</Button>}
      </div>
    </div>
  )

  return (
    <RecordSection
      id="quotes"
      tour="records.record.quotes"
      title="Quote versions"
      summary={readSummary(quotes, (d) => quotesSummary(d?.versions))}
      defaultOpen={owned.has('quotes') || waitingResponses.length > 0}
      activeTaskFor={owned.has('quotes') ? orderId : null}
      focused={focused}
    >
      {quotes.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {readUnavailable(quotes) && <p className="text-sm text-destructive">{recordErrorMessage(quotes.error)}</p>}
      {quotes.data && versions.length === 0 && (
        <p className="text-sm text-muted-foreground">No quote has been prepared on this record.</p>
      )}
      {versions.length > 0 && (
        <ul className="space-y-2 mb-3">
          {versions.map(v => {
            const c = chip(v)
            const isPrepared = v.status === 'prepared'
            const draft = isPrepared ? v.draft : null
            const sending = draft?.status === 'sending'
            const pending = draft?.status === 'pending'
            // The mail went out but the mark after the absorb was refused: marking it sent another
            // way is the recovery, never a second email.
            const sentUnmarked = draft?.status === 'sent'
            return (
              <li key={v.id} className="text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-foreground">
                    <span className="font-mono text-xs text-muted-foreground">v{v.versionNumber}</span>{' '}
                    <span className="font-medium">{formatCurrency(v.amountCents, v.currency)}</span>
                  </span>
                  <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', c.tone)}>{c.text}</span>
                </div>
                <p className="text-xs text-muted-foreground">{!isPrepared && v.sentAt ? text.sentLine(v, now, zone) : text.preparedLine(v, now, zone)}</p>
                {v.terms && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{v.terms}</p>}
                {v.response?.note && <p className="text-xs italic text-muted-foreground">“{v.response.note}”</p>}
                {(v.document || (isPrepared && open)) && (
                  <div className="mt-1 flex flex-wrap gap-2">
                    {v.document && <Button size="sm" variant="secondary" isLoading={viewBusy === v.versionNumber} onClick={() => void handleView(v)}>{text.VIEW}</Button>}
                    {v.document && <Button size="sm" variant="secondary" onClick={() => void handleDownload(v)}>{text.DOWNLOAD}</Button>}
                    {isPrepared && open && !sendRefusal && !sending && !sentUnmarked && !editing && <Button size="sm" variant="secondary" disabled={busy} onClick={startEdit}>{text.EDIT}</Button>}
                    {isPrepared && open && !sendRefusal && !sending && !confirming && !editing && (
                      <Button size="sm" variant="secondary" disabled={busy} onClick={() => setConfirming(true)}>{sentUnmarked ? text.MARK_EMAILED : text.MARK_SENT}</Button>
                    )}
                  </div>
                )}
                {isPrepared && open && !sendRefusal && (
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    {sending && <span>{text.DRAFT_SENDING}</span>}
                    {pending && draft && (
                      <>
                        <span>{text.DRAFT_READY}</span>
                        <Link to={`/inbox/${draft.conversationId}`} className={buttonVariants({ variant: 'outline', size: 'sm' })}>{text.OPEN_DRAFT}</Link>
                      </>
                    )}
                    {sentUnmarked && draft && <span>{text.draftSentUnmarked(whenText(draft.dispatchedAt ?? draft.sentAt, now, zone))}</span>}
                    {!sending && !pending && !sentUnmarked && draftRefusal && <span>{text.draftRefusalText(draftRefusal)}</span>}
                    {!sending && !pending && !sentUnmarked && !draftRefusal && (
                      <Button size="sm" variant="secondary" disabled={busy} onClick={() => handleRedraft(v)}>
                        {draft ? text.DRAFT_AGAIN : text.DRAFT_EMAIL}
                      </Button>
                    )}
                  </div>
                )}
                {isPrepared && open && draft?.id === keptDraftId && draft?.status === 'pending' && draft.edited && <p className="mt-1 text-xs text-warning-strong">{text.DRAFT_KEEPS_TEXT}</p>}
                {isPrepared && !sendRefusal && editing && <div className="mt-2">{form(text.SAVE_EDIT, handleSaveEdit, resetForm)}</div>}
                {isPrepared && !sendRefusal && confirming && (
                  <div className="mt-2 space-y-2">
                    <Textarea aria-label={text.NOTE_LABEL} rows={2} placeholder={text.NOTE_LABEL} value={note} onChange={e => setNote(e.target.value)} />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={handleMarkSent} disabled={busy}>{text.CONFIRM_SENT}</Button>
                      <Button size="sm" variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>{text.CANCEL_EDIT}</Button>
                    </div>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {downloadError && <p className="mb-3 text-sm text-destructive">{downloadError}</p>}
      {viewing && (
        <MediaViewerDialog
          items={[{ kind: 'pdf', blobUrl: viewing.url, filename: viewing.name, byteSize: viewing.size }]}
          index={0}
          onClose={() => setViewing(null)}
        />
      )}
      {open && (update.error || markSent.error || redraft.error) && (
        <p className="mb-3 text-sm text-destructive">{recordErrorMessage(update.error ?? markSent.error ?? redraft.error)}</p>
      )}

      {waitingResponses.map(w => {
        const cannot = cannotRecord(w, { open, versions: quotes.data?.versions ?? [], respondRefusal })
        const kind = responseKindOf(w.intent)
        return (
          <div key={w.id} className="border-t border-border pt-3 mb-3 space-y-2" data-testid="waiting-response">
            <p className="text-xs text-muted-foreground">{updateBlockHeading(w)}</p>
            {w.excerpt && <p className="text-sm text-foreground whitespace-pre-wrap">{w.excerpt}</p>}
            {cannot && cannot.reason !== 'refused' && <p className="text-xs text-muted-foreground">{cannotRecordText(cannot, null)  /* 'refused' is printed once, below, by the card */}</p>}
            <div className="flex flex-wrap items-center gap-2">
              {!cannot && kind && responding !== kind && (
                <Button size="sm" disabled={busy} onClick={() => handleRecordWaiting(kind)}>{RESPONSE_SUBMIT[kind]}</Button>
              )}
              {dismissing !== w.id && (
                <Button size="sm" variant="ghost" disabled={dismiss.isPending} onClick={() => { setDismissing(w.id); setDismissReason('') }}>{DISMISS}</Button>
              )}
              {w.conversationId && (
                <Link to={`/inbox/${w.conversationId}${w.messageId ? `?message=${w.messageId}` : ''}`} className="text-xs text-primary underline-offset-2 hover:underline">{OPEN_IN_INBOX}</Link>
              )}
            </div>
            {dismissing === w.id && (
              <div className="flex flex-wrap items-end gap-2">
                <Input id={`dismiss-${w.id}`} label="Reason for dismissing" value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} placeholder="optional" maxLength={2000} />
                <Button size="sm" disabled={dismiss.isPending} onClick={() => handleDismissWaiting(w.id)}>Confirm dismiss</Button>
                <Button size="sm" variant="ghost" onClick={() => { setDismissing(null); dismiss.reset() }}>Keep</Button>
              </div>
            )}
            {dismiss.error && dismissing === w.id && <p className="text-sm text-destructive">{dismissErrorMessage(dismiss.error)}</p>}
          </div>
        )
      })}
      {answerable && respondRefusal && (
        <p className="border-t border-border pt-3 mb-3 text-xs text-muted-foreground">
          {text.respondRefusalText(respondRefusal)}
        </p>
      )}
      {answerable && !respondRefusal && (
        <div className="border-t border-border pt-3 mb-3 space-y-2">
          <p className="text-xs text-muted-foreground">The customer’s answer to v{answerable.versionNumber}:</p>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(RESPONSE_BUTTON) as ResponseKind[]).map(kind => (
              <Button key={kind} size="sm" variant={responding === kind ? 'default' : 'secondary'}
                aria-pressed={responding === kind} disabled={busy}
                onClick={() => setResponding(responding === kind ? null : kind)}>
                {RESPONSE_BUTTON[kind]}
              </Button>
            ))}
          </div>
          {responding && (
            <div className="space-y-2">
              {responding === 'counter' && (
                <div className="flex items-center gap-2">
                  <Input ref={amountRef} aria-label="Counter amount" type="number" min={0} step="0.01" placeholder="Their amount"
                    value={responseAmount} onChange={e => setResponseAmount(e.target.value)} />
                  <span className="text-sm text-muted-foreground">{answerable.currency}</span>
                </div>
              )}
              <Textarea ref={noteRef} aria-label="Response note" rows={2} placeholder="What they said (optional)"
                value={responseNote} onChange={e => setResponseNote(e.target.value)} />
              <Button size="sm" onClick={handleRespond}
                disabled={busy || (responding === 'counter' && toCents(responseAmount) == null)}>
                {RESPONSE_SUBMIT[responding]}
              </Button>
            </div>
          )}
        </div>
      )}
      {/* SA-1016: outside the buttons, because a refused answer's refetch
          swaps them for the line and the press must still be acknowledged. */}
      {open && respond.error && <p className="mb-3 text-sm text-destructive">{recordErrorMessage(respond.error)}</p>}

      {open && sendRefusal && (
        <p className="border-t border-border pt-3 text-xs text-muted-foreground">
          {text.sendRefusalText(sendRefusal)}
        </p>
      )}
      {/* SA-1419: only once the read has landed, so a step-13 record never flashes the form. */}
      {open && quotes.data && !prepared && !sendRefusal && (
        <div className="border-t border-border pt-3 space-y-2">
          <p className="text-sm font-medium text-foreground">{text.PREPARE_TITLE}</p>
          <p className="text-xs text-muted-foreground">{text.prepareSentence(draftRefusal)}</p>
          {form(text.PREPARE_BUTTON, handlePrepare)}
        </div>
      )}
      {/* SA-1003: outside the form, because a refused prepare's refetch swaps the
          form for the line and the press must still be acknowledged. */}
      {open && prepare.error && <p className="mt-2 text-sm text-destructive">{recordErrorMessage(prepare.error)}</p>}
    </RecordSection>
  )
}
